
import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';

const AdHero: React.FC = () => {
  return (
    <section className="py-20 px-6 md:px-10 bg-gray-900 text-white relative overflow-hidden">
      {/* Enhanced Background Animation */}
      <div className="absolute inset-0 bg-[#010b20] z-0 overflow-hidden">
        {/* Animated wave element */}
        <div className="absolute w-full h-full overflow-hidden">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent transform -translate-y-1/2 animate-wave"></div>
          <div className="absolute top-[45%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent transform -translate-y-1/2 animate-wave-slow"></div>
          <div className="absolute top-[55%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-300/20 to-transparent transform -translate-y-1/2 animate-wave-slower"></div>
          
          {/* Glowing point that emits the waves */}
          <div className="absolute top-1/2 left-[20%] w-2 h-2 rounded-full bg-white/90 shadow-[0_0_30px_15px_rgba(59,130,246,0.5)] animate-pulse-gentle transform -translate-y-1/2"></div>
          
          {/* Radial rays emanating from the glowing point */}
          <div className="absolute top-1/2 left-[20%] w-20 h-20 animate-rays-rotate transform -translate-x-1/2 -translate-y-1/2">
            {Array.from({ length: 12 }).map((_, index) => (
              <div 
                key={index} 
                className="absolute top-1/2 left-1/2 h-[1px] w-10 bg-gradient-to-r from-blue-400/90 to-transparent origin-left"
                style={{ transform: `rotate(${index * 30}deg)` }}
              ></div>
            ))}
          </div>
        </div>
        
        {/* Ambient glow overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/30 to-purple-900/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent blur-3xl"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-purple-500/10 via-transparent to-transparent blur-3xl"></div>
      </div>
      
      {/* Logo in top left */}
      <div className="absolute top-8 left-8 z-10">
        <img 
          src="/lovable-uploads/54ccb2dd-9554-4999-9a41-1ea1e0dc3159.png" 
          alt="Sparkwave AI Logo" 
          className="h-12 md:h-16"
        />
      </div>
      
      <div className="container mx-auto max-w-5xl text-center relative z-10">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up opacity-0" style={{animationDelay: "200ms", animationFillMode: "forwards"}}>
          Doing Nothing Is the Risky Move.
        </h1>
        <p className="text-xl md:text-2xl text-white/80 mb-6 max-w-4xl mx-auto animate-fade-in-up opacity-0" style={{animationDelay: "400ms", animationFillMode: "forwards"}}>
          While you're stuck deciding, your competitors are automating lead follow-ups, 
          customer support, content, and analytics. We help you catch up—fast.
        </p>
        
        <h2 className="text-2xl md:text-3xl font-bold mb-4 animate-fade-in-up opacity-0" style={{animationDelay: "500ms", animationFillMode: "forwards"}}>
          AI That Fixes What's Holding You Back
        </h2>
        
        <p className="text-md text-white/60 mb-10 max-w-3xl mx-auto animate-fade-in-up opacity-0" style={{animationDelay: "600ms", animationFillMode: "forwards"}}>
          We build AI agents that save time, boost profits, and eliminate manual work.
          <br />
          <span className="font-bold">Get fast prototypes. Quick wins. Real ROI.</span>
        </p>
        
        <div className="animate-fade-in-up opacity-0" style={{animationDelay: "800ms", animationFillMode: "forwards"}}>
          <Button 
            size="lg" 
            className="bg-heading-accent hover:bg-heading-accent/90 text-white group btn-hover-effect transition-all duration-300" 
            onClick={() => {
              const formSection = document.getElementById("lead-form");
              formSection?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Get Your Free Consultation
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AdHero;
